import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  FolderKanban,
  MessageSquare,
  Mail,
  MailOpen,
  Download,
  Trash2,
  Plus,
  Pencil,
  X,
  Check,
  Clock,
  CheckCircle2,
  Circle,
  AlertCircle,
  Lock,
  LogOut,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Login Component
const LoginScreen = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem('adminToken', response.data.token);
      onLogin(response.data.token);
    } catch (err) {
      setError('Incorrect password');
      setPassword('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
      >
        <div className="bg-card border border-border/50 rounded-lg p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <h1 className="font-['Sora'] text-2xl font-bold text-foreground">Admin Access</h1>
            <p className="text-sm text-muted-foreground mt-2">Enter password to continue</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                placeholder="Enter admin password"
                className="bg-input"
                autoFocus
                disabled={isLoading}
              />
              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}
            </div>
            <Button type="submit" className="w-full bg-primary" disabled={isLoading}>
              {isLoading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

// Status configurations
const statusConfig = {
  'Not Started': { color: 'bg-gray-500', icon: Circle, progress: 0 },
  'In Progress': { color: 'bg-blue-500', icon: Clock, progress: 50 },
  'Almost Done': { color: 'bg-yellow-500', icon: AlertCircle, progress: 80 },
  'Completed': { color: 'bg-green-500', icon: CheckCircle2, progress: 100 },
};

// Stat Card
const StatCard = ({ title, value, icon: Icon, color = 'primary' }) => (
  <div className="bg-card border border-border/50 rounded-lg p-5">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-muted-foreground">{title}</p>
        <p className="text-3xl font-['Sora'] font-bold text-foreground mt-1">{value}</p>
      </div>
      <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center">
        <Icon className="w-5 h-5 text-primary" />
      </div>
    </div>
  </div>
);

// Progress Bar
const ProgressBar = ({ progress, status }) => (
  <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
    <div
      className={`h-full transition-all duration-300 ${statusConfig[status]?.color || 'bg-gray-500'}`}
      style={{ width: `${progress}%` }}
    />
  </div>
);

// Project Row
const ProjectRow = ({ project, onEdit, onDelete }) => {
  const StatusIcon = statusConfig[project.status]?.icon || Circle;
  const progress = project.progress || statusConfig[project.status]?.progress || 0;

  return (
    <div className="bg-card border border-border/50 rounded-lg p-4 hover:border-border transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="font-semibold text-foreground truncate">{project.name}</h3>
            <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary flex-shrink-0">
              {project.category}
            </span>
          </div>
          
          <div className="flex items-center gap-4 mb-3">
            <div className="flex items-center gap-1.5">
              <StatusIcon className={`w-4 h-4 ${statusConfig[project.status]?.color.replace('bg-', 'text-')}`} />
              <span className="text-sm text-muted-foreground">{project.status}</span>
            </div>
            <span className="text-sm text-muted-foreground">{progress}%</span>
          </div>
          
          <ProgressBar progress={progress} status={project.status} />
          
          {project.notes && (
            <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{project.notes}</p>
          )}
        </div>
        
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button size="sm" variant="ghost" onClick={() => onEdit(project)}>
            <Pencil className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => onDelete(project.id)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Project Form Modal
const ProjectForm = ({ project, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: project?.name || '',
    category: project?.category || 'Branding',
    status: project?.status || 'Not Started',
    progress: project?.progress || 0,
    notes: project?.notes || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...formData, id: project?.id });
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-card border border-border rounded-lg p-6 w-full max-w-md"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-['Sora'] text-xl font-semibold text-foreground">
            {project ? 'Edit Project' : 'Add Project'}
          </h2>
          <Button size="sm" variant="ghost" onClick={onCancel}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Project Name</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Brand Identity for Client X"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Branding">Branding</SelectItem>
                <SelectItem value="Social Media">Social Media</SelectItem>
                <SelectItem value="Creative Design">Creative Design</SelectItem>
                <SelectItem value="Web Design & Development">Web Design & Development</SelectItem>
                <SelectItem value="Sports Design">Sports Design</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select 
                value={formData.status} 
                onValueChange={(v) => setFormData({ 
                  ...formData, 
                  status: v,
                  progress: statusConfig[v]?.progress || formData.progress
                })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Not Started">Not Started</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Almost Done">Almost Done</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Progress ({formData.progress}%)</Label>
              <Input
                type="range"
                min="0"
                max="100"
                value={formData.progress}
                onChange={(e) => setFormData({ ...formData, progress: parseInt(e.target.value) })}
                className="h-10"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Notes (what remains to be done)</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="e.g., Waiting for client feedback on logo concepts..."
              rows={3}
            />
          </div>
          
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
            <Button type="submit" className="bg-primary">{project ? 'Update' : 'Add'} Project</Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Message Item
const MessageItem = ({ message, isSelected, onClick, onToggleRead }) => (
  <div
    onClick={onClick}
    className={`p-4 border-b border-border/30 cursor-pointer transition-colors ${
      isSelected ? 'bg-primary/5' : 'hover:bg-card'
    } ${!message.is_read ? 'bg-card' : ''}`}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-foreground text-sm truncate">{message.full_name}</span>
          {!message.is_read && <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />}
        </div>
        <p className="text-xs text-primary truncate">{message.service}</p>
        <p className="text-xs text-muted-foreground mt-1">{new Date(message.created_at).toLocaleDateString()}</p>
      </div>
      <Button
        size="sm"
        variant="ghost"
        onClick={(e) => { e.stopPropagation(); onToggleRead(message.id); }}
        className="flex-shrink-0"
      >
        {message.is_read ? <Mail className="w-4 h-4" /> : <MailOpen className="w-4 h-4" />}
      </Button>
    </div>
  </div>
);

// Main Admin Dashboard
export const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('projects');
  const [projects, setProjects] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [showProjectForm, setShowProjectForm] = useState(false);

  // Check authentication on mount
  useEffect(() => {
    const verifyAuth = async () => {
      const token = localStorage.getItem('adminToken');
      if (token) {
        try {
          await axios.get(`${API}/admin/verify`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAuthToken(token);
          setIsAuthenticated(true);
        } catch (err) {
          localStorage.removeItem('adminToken');
        }
      }
      setIsLoading(false);
    };
    verifyAuth();
  }, []);

  // Logout handler
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
    } catch (err) {
      // Ignore errors
    }
    localStorage.removeItem('adminToken');
    setAuthToken(null);
    setIsAuthenticated(false);
  };

  // API helper with auth
  const authHeaders = { headers: { Authorization: `Bearer ${authToken}` } };

  // Fetch data
  const fetchData = async () => {
    if (!authToken) return;
    try {
      const [projectsRes, messagesRes] = await Promise.all([
        axios.get(`${API}/admin/projects`, authHeaders),
        axios.get(`${API}/admin/messages`, authHeaders),
      ]);
      setProjects(projectsRes.data);
      setMessages(messagesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  useEffect(() => {
    if (isAuthenticated && authToken) {
      fetchData();
    }
  }, [isAuthenticated, authToken]);

  // Show loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <LoginScreen onLogin={(token) => { setAuthToken(token); setIsAuthenticated(true); }} />;
  }

  // Project handlers
  const handleSaveProject = async (projectData) => {
    try {
      if (projectData.id) {
        await axios.put(`${API}/admin/projects/${projectData.id}`, projectData, authHeaders);
      } else {
        await axios.post(`${API}/admin/projects`, projectData, authHeaders);
      }
      fetchData();
      setShowProjectForm(false);
      setEditingProject(null);
    } catch (error) {
      console.error('Error saving project:', error);
    }
  };

  const handleDeleteProject = async (id) => {
    if (window.confirm('Delete this project?')) {
      try {
        await axios.delete(`${API}/admin/projects/${id}`, authHeaders);
        fetchData();
      } catch (error) {
        console.error('Error deleting project:', error);
      }
    }
  };

  // Message handlers
  const handleToggleRead = async (id) => {
    try {
      await axios.put(`${API}/admin/messages/${id}/read`, {}, authHeaders);
      fetchData();
    } catch (error) {
      console.error('Error toggling read status:', error);
    }
  };

  const handleExportMessages = () => {
    const blob = new Blob([JSON.stringify(messages, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `messages-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
  };

  // Stats
  const stats = {
    totalProjects: projects.length,
    inProgress: projects.filter(p => p.status === 'In Progress' || p.status === 'Almost Done').length,
    unreadMessages: messages.filter(m => !m.is_read).length,
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Header */}
      <header className="border-b border-border/50 bg-card">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="font-['Sora'] text-xl font-semibold text-foreground">DDesign Admin</h1>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-muted-foreground hover:text-foreground">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <StatCard title="Total Projects" value={stats.totalProjects} icon={FolderKanban} />
          <StatCard title="In Progress" value={stats.inProgress} icon={Clock} />
          <StatCard title="Unread Messages" value={stats.unreadMessages} icon={MessageSquare} />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'projects' ? 'default' : 'outline'}
            onClick={() => setActiveTab('projects')}
            className={activeTab === 'projects' ? 'bg-primary' : ''}
          >
            <FolderKanban className="w-4 h-4 mr-2" /> Projects
          </Button>
          <Button
            variant={activeTab === 'messages' ? 'default' : 'outline'}
            onClick={() => setActiveTab('messages')}
            className={activeTab === 'messages' ? 'bg-primary' : ''}
          >
            <MessageSquare className="w-4 h-4 mr-2" /> 
            Messages
            {stats.unreadMessages > 0 && (
              <span className="ml-2 bg-destructive text-destructive-foreground text-xs px-1.5 py-0.5 rounded-full">
                {stats.unreadMessages}
              </span>
            )}
          </Button>
        </div>

        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="font-['Sora'] text-lg font-semibold text-foreground">Project Tracking</h2>
              <Button className="bg-primary" onClick={() => { setEditingProject(null); setShowProjectForm(true); }}>
                <Plus className="w-4 h-4 mr-2" /> Add Project
              </Button>
            </div>

            {projects.length > 0 ? (
              <div className="space-y-3">
                {projects.map((project) => (
                  <ProjectRow
                    key={project.id}
                    project={project}
                    onEdit={(p) => { setEditingProject(p); setShowProjectForm(true); }}
                    onDelete={handleDeleteProject}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <FolderKanban className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No projects yet. Add your first project to start tracking.</p>
              </div>
            )}
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="font-['Sora'] text-lg font-semibold text-foreground">Messages</h2>
              <Button variant="outline" onClick={handleExportMessages}>
                <Download className="w-4 h-4 mr-2" /> Export
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Messages List */}
              <div className="lg:col-span-1 bg-card border border-border/50 rounded-lg overflow-hidden max-h-[600px] overflow-y-auto">
                {messages.length > 0 ? (
                  messages.map((msg) => (
                    <MessageItem
                      key={msg.id}
                      message={msg}
                      isSelected={selectedMessage?.id === msg.id}
                      onClick={() => setSelectedMessage(msg)}
                      onToggleRead={handleToggleRead}
                    />
                  ))
                ) : (
                  <div className="p-8 text-center text-muted-foreground">
                    <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-50" />
                    <p>No messages yet</p>
                  </div>
                )}
              </div>

              {/* Message Detail */}
              <div className="lg:col-span-2 bg-card border border-border/50 rounded-lg p-6">
                {selectedMessage ? (
                  <div className="space-y-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-['Sora'] text-xl font-semibold text-foreground">{selectedMessage.full_name}</h3>
                        <p className="text-sm text-muted-foreground">{selectedMessage.email}</p>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(selectedMessage.created_at).toLocaleString()}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-4 py-4 border-y border-border/30">
                      <div>
                        <p className="text-xs text-muted-foreground">Business</p>
                        <p className="text-sm text-foreground">{selectedMessage.business_name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Service</p>
                        <p className="text-sm text-foreground">{selectedMessage.service}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Budget</p>
                        <p className="text-sm text-foreground">{selectedMessage.budget}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Deadline</p>
                        <p className="text-sm text-foreground">{selectedMessage.deadline}</p>
                      </div>
                    </div>

                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Project Description</p>
                      <p className="text-sm text-foreground whitespace-pre-wrap">{selectedMessage.project_description}</p>
                    </div>

                    {selectedMessage.inspiration && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-2">Inspiration</p>
                        <p className="text-sm text-foreground">{selectedMessage.inspiration}</p>
                      </div>
                    )}

                    <div className="pt-4">
                      <a href={`mailto:${selectedMessage.email}`}>
                        <Button className="bg-primary">Reply via Email</Button>
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64 text-muted-foreground">
                    Select a message to view details
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Project Form Modal */}
      {showProjectForm && (
        <ProjectForm
          project={editingProject}
          onSave={handleSaveProject}
          onCancel={() => { setShowProjectForm(false); setEditingProject(null); }}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
